<template>
  <my-giving-layout :enable-scroll-event="true">
    <div
      ref="contentContainer"
      class="bg-[#F6F4F0] min-h-screen w-full lg:max-w-[600px] lg:mx-auto font-inter"
      :class="shouldFixButton ? 'pb-[120px]' : 'pb-[64px]'"
    >
      <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
      <div ref="contentArea" class="flex flex-col px-[20px] pb-[64px]">
        <!-- Page Title -->
        <h1
          class="text-[2rem] md:text-[3rem] lg:text-[3.25rem] leading-[2.5rem] md:leading-[3.5rem] lg:leading-[4rem] font-black text-[#21201E] mb-[16px] tracking-[-0.03em]"
        >
          I'm giving to... 💰
        </h1>
        <BaseSelect
          v-model="givingType"
          class="w-full mb-[40px]"
          :options="funds"
          placeholder="Select an option"
          width-classes="border-1 w-full border-[#C3C2C1] bg-[#FFFFFF]"
          label-classes="w-full text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-semibold text-[#191817]"
          text-classes="w-full text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-normal text-[#21201E]"
        />

        <!-- Select giving method -->
        <div v-if="givingType">
          <h2
            class="text-[1.25rem] md:text-[2rem] lg:text-[2.25rem] leading-[1.75rem] md:leading-[2.5rem] lg:leading-[3rem] font-black text-[#21201E] mb-[24px] tracking-[-0.03em]"
          >
            {{ isFromRecurring ? 'Select linked bank account' : 'Select giving method' }}
          </h2>

          <!-- Linked giving methods -->
          <div class="flex flex-col gap-[12px] mb-[24px]">
            <ion-list class="!bg-[#F6F4F0] rounded-[12px] mb-[12px]">
              <ion-radio-group v-model="givingMethod">
                <div v-if="linkedBankAccounts.length">
                  <p
                    class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-[600] text-gray-600 mb-[12px]"
                  >
                    Linked accounts
                  </p>
                  <div class="flex flex-col gap-[12px]">
                    <RadioCardSelection
                      v-for="account in linkedBankAccounts"
                      :key="account.eGiroMandateId"
                      :bank-code="account.bankCode"
                      :account-number="account.maskedBankAccNumber"
                      :value="account.eGiroMandateId"
                      :model-value="givingMethod"
                      name="giving-selection"
                      @update:model-value="givingMethod = $event"
                    />
                  </div>
                </div>
                <div v-else class="flex flex-col gap-[12px] mb-[24px]">
                  <button
                    @click="navigateToListOfBanks"
                    class="relative w-full p-[16px] rounded-[10px] text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] font-[500] text-gray-900 text-left flex items-center cursor-pointer overflow-visible"
                  >
                    <svg
                      class="absolute inset-0 w-full h-full pointer-events-none"
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <!-- Mask keeps stroke fully inside bounds -->
                        <clipPath id="clip-rounded">
                          <rect
                            x="1"
                            y="1"
                            width="calc(100% - 2px)"
                            height="calc(100% - 2px)"
                            rx="9"
                            ry="9"
                          />
                        </clipPath>
                      </defs>

                      <rect
                        clip-path="url(#clip-rounded)"
                        x="1"
                        y="1"
                        width="calc(100% - 2px)"
                        height="calc(100% - 2px)"
                        rx="9"
                        ry="9"
                        fill="none"
                        stroke="#A5A4A3"
                        stroke-width="1.9"
                        stroke-dasharray="3 3"
                        stroke-linecap="butt"
                        vector-effect="non-scaling-stroke"
                        shape-rendering="crispEdges"
                      />
                    </svg>

                    <img src="/assets/icons/plus.svg" alt="plus-icon" class="mr-[12px]" />
                    Add bank account
                  </button>

                  <!-- Show message when coming from recurring and no linked accounts -->
                  <div
                    v-if="isFromRecurring"
                    class="mt-[16px] p-[16px] bg-blue-50 rounded-[12px] border border-blue-200"
                  >
                    <p
                      class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-blue-800"
                    >
                      <strong>Recurring giving setup:</strong> You need to link a bank account to
                      set up recurring giving. Other payment methods are not available for recurring
                      donations.
                    </p>
                  </div>
                </div>

                <div
                  v-if="bankAccountsPendingApproval.length"
                  class="mt-[12px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-inter"
                >
                  <p class="italic text-[#63615E]">
                    You have {{ bankAccountsPendingApproval.length }} pending linked methods.
                    <span
                      ><router-link to="/my-giving/manage" class="text-[#454BEF] underline"
                        >View all</router-link
                      ></span
                    >
                  </p>
                </div>

                <!-- Other giving methods - only show if not from recurring -->
                <div v-if="!isFromRecurring">
                  <p
                    class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] font-[600] text-gray-600 mt-[24px] mb-[12px]"
                  >
                    Other giving methods
                  </p>
                  <div class="flex flex-col gap-[12px]">
                    <GivingMethodCard
                      v-for="method in methods"
                      :key="method.value"
                      :label="method.label"
                      :icon="method.icon"
                      :value="method.value"
                      :model-value="givingMethod"
                      name="giving-selection"
                      @update:model-value="givingMethod = $event"
                    />
                  </div>
                </div>
              </ion-radio-group>
            </ion-list>
          </div>
        </div>
      </div>

      <!-- Continue Button - In normal flow when not fixed -->
      <div
        v-if="!shouldFixButton && givingType && givingMethod"
        ref="normalButton"
        class="px-[20px] md:px-[24px] lg:px-[28px] mt-[24px] mb-[64px]"
      >
        <div class="max-w-[600px] mx-auto">
          <BottomButton
            text="Continue"
            :disabled="isButtonDisabled"
            @click="handleContinue"
            class="w-full"
          />
        </div>
      </div>
    </div>

    <!-- Fixed Button - Outside content flow when fixed -->
    <div
      v-if="shouldFixButton && givingType && givingMethod"
      ref="fixedButton"
      :key="'fixed-button'"
      class="fixed bottom-0 left-0 right-0 px-[20px] md:px-[24px] lg:px-[28px] py-[16px] z-10"
    >
      <div class="max-w-[600px] mx-auto">
        <BottomButton
          text="Continue"
          :disabled="isButtonDisabled"
          @click="handleContinue"
          class="w-full"
        />
      </div>
    </div>
  </my-giving-layout>
</template>

<script>
import { saveFastSelection } from '@/utils/fast-selection';
import BackButton from '@/components/common/BackButton.vue';
import BaseSelect from '@/components/design/BaseSelect.vue';
import BottomButton from '@/components/common/BottomButton.vue';
import { isBarePath } from '@/utils/banner-link';
import { openUrl } from '@/utils/browser-helper';
import { mapGetters } from 'vuex';
import { IonRadioGroup, IonList } from '@ionic/vue';
import RadioCardSelection from '@/components/MyGiving/Common/RadioCardSelection.vue';
import GivingMethodCard from '@/components/MyGiving/Common/GivingMethodCard.vue';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
export default {
  name: 'GivingOptions',
  components: {
    BackButton,
    BaseSelect,
    BottomButton,
    IonRadioGroup,
    IonList,
    RadioCardSelection,
    GivingMethodCard,
    MyGivingLayout,
  },
  data() {
    return {
      givingType: null,
      givingMethod: null,
      selectedLinkedAccount: null,
      isFromGivenNow: false,
      isFromRecurring: false,
      shouldFixButton: false,
      viewportHeight: 0,
      isSubmitting: false,
      funds: [
        'Tithe',
        'Offering',
        'Building Fund',
        'Missions',
        'Heart Community Services',
        'GenerationS Fund',
      ],
      methods: [
        {
          value: 'paynow-qr',
          label: 'PayNow QR',
          icon: 'qr',
        },
        {
          value: 'credit-card',
          label: 'Credit Card',
          icon: 'card',
        },
        {
          value: 'local-bank-transfer',
          label: 'Local Bank Transfer',
          icon: 'local',
        },
        {
          value: 'overseas-banking',
          label: 'Overseas Banking',
          icon: 'overseas',
        },
      ],
    };
  },
  async ionViewWillEnter() {
    // A caller can preselect the fund (the BF2026 pledge page sends
    // `fund=Building Fund`); anything not in the list is ignored.
    const { fund } = this.$route.query;
    if (this.funds.includes(fund)) this.givingType = fund;
    // A one-time pledge's amount is for the Give now it opened (it sends
    // `prefill=pledge`), not for one opened from anywhere else: a recurring set-up,
    // or the BF2026 QR, which names the fund too but carries no amount.
    if (this.$route.query.prefill !== 'pledge') {
      this.$store.commit('myGiving/setGiveNowPrefill', null);
    }

    await this.$store.dispatch('myGiving/getLinkedBankAccounts');
    this.isFromGivenNow = !!window.location.search;
    this.isFromRecurring =
      this.$route.query.fromrecurring === 'true' || this.$route.query.fromrecurring === true;

    this.$nextTick(() => {
      this.checkIfShouldFixButton();
    });
  },
  mounted() {
    this.viewportHeight = window.innerHeight || window.screen.height;
    window.addEventListener('resize', this.handleResize);

    this.$nextTick(() => {
      setTimeout(() => {
        this.checkIfShouldFixButton();
      }, 100);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    ...mapGetters('myGiving', ['linkedBankAccounts', 'bankAccountsPendingApproval']),
    isButtonDisabled() {
      return this.isSubmitting || !this.givingType || !this.givingMethod;
    },
    knownGivingMethodValues() {
      return this.methods.map(method => method.value);
    },
  },
  methods: {
    back() {
      // A caller can name where back goes: a BF2026 one-time pledge sends
      // `back=/my-giving`, as its pledge is made and the form is not to be reopened.
      // In-app paths only.
      const { back } = this.$route.query;
      if (typeof back === 'string' && isBarePath(back)) {
        this.$router.replace(back);
      } else if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving');
      }
    },
    navigateTo(route) {
      this.$router.push(route);
    },
    navigateToListOfBanks() {
      if (this.isFromRecurring) {
        this.$router.push({ path: '/my-giving/list-of-banks', query: { fromrecurring: true } });
      } else {
        this.$router.push('/my-giving/list-of-banks');
      }
    },
    async handleContinue() {
      if (this.isButtonDisabled) return;

      this.isSubmitting = true;

      try {
        switch (this.givingMethod) {
          case 'paynow-qr':
            this.$store.commit('myGiving/setGivingType', this.givingType);
            this.$store.commit('myGiving/setGivingMethod', this.givingMethod);
            this.navigateTo('/my-giving/paynow');
            break;
          case 'credit-card':
            await openUrl(
              { url: 'https://donate.stripe.com/aEUaFwbDBggm3pSeUU' },
              {
                title: 'Stripe Donation',
                source: 'giving-options',
                page: 'MyGiving',
              },
            );
            break;
          case 'local-bank-transfer':
            this.$store.commit('myGiving/setGivingType', this.givingType);
            this.navigateTo('/my-giving/local-transfer');
            break;
          case 'overseas-banking':
            this.$store.commit('myGiving/setGivingType', this.givingType);
            this.navigateTo('/my-giving/overseas-transfer');
            break;
          default: {
            if (this.knownGivingMethodValues.includes(this.givingMethod)) {
              this.$store.dispatch('toast/error', 'Please select a valid giving method.');
              this.givingMethod = null;
              return;
            }

            const selectedLinkedAccount = this.linkedBankAccounts.find(
              item => item.eGiroMandateId === this.givingMethod,
            );

            if (!selectedLinkedAccount) {
              this.$store.dispatch(
                'toast/error',
                'This linked account is no longer available. Please select another account.',
              );
              this.givingMethod = null;
              return;
            }

            this.$store.commit('myGiving/setGivingType', this.givingType);
            this.$store.commit('myGiving/setGivingMethod', selectedLinkedAccount.bankCode);
            this.$store.commit(
              'myGiving/setGivingAccount',
              selectedLinkedAccount.maskedBankAccNumber,
            );
            this.$store.commit('myGiving/setEGiroMandateId', selectedLinkedAccount.eGiroMandateId);
            this.$store.commit(
              'myGiving/setAvailableLimitForSelectedAccount',
              selectedLinkedAccount.maxAmount,
            );
            this.$store.commit('myGiving/setIsFromGivenNow', this.isFromRecurring);
            // Kept for the FAST page to restore after a WebView reload.
            saveFastSelection({
              memberId: this.$store.state.profile?.id,
              eGiroMandateId: selectedLinkedAccount.eGiroMandateId,
              giveType: this.givingType,
              isFromGivenNow: this.isFromRecurring,
            });
            this.$router.replace('/my-giving/local-transfer-egiro');
            break;
          }
        }
        this.givingMethod = null;
        this.givingType = null;
      } finally {
        this.isSubmitting = false;
      }
    },
    checkIfShouldFixButton() {
      // On landscape iPad, always use inline button
      if (window.innerWidth > window.innerHeight && window.innerWidth >= 1024) {
        this.shouldFixButton = false;
        return;
      }

      if (!this.$refs.contentContainer || !this.$refs.contentArea) {
        return;
      }

      if (!this.viewportHeight || this.viewportHeight === 0) {
        this.viewportHeight = window.innerHeight || window.screen.height;
      }

      this.$nextTick(() => {
        if (!this.$refs.contentArea) {
          return;
        }

        const contentAreaScrollHeight = this.$refs.contentArea?.scrollHeight || 0;
        if (!contentAreaScrollHeight || contentAreaScrollHeight === 0) {
          this.shouldFixButton = false;
          return;
        }

        const backButtonHeight = 48;
        const buttonHeight = window.innerWidth >= 1024 ? 72 : window.innerWidth >= 768 ? 64 : 56;
        const buttonMarginTop = 24;
        const buttonMarginBottom = 64;
        const buttonCurrentlyInContent = !!this.$refs.normalButton;

        let contentHeightWithoutButton;
        if (buttonCurrentlyInContent) {
          contentHeightWithoutButton =
            contentAreaScrollHeight - buttonHeight - buttonMarginTop - buttonMarginBottom;
        } else {
          contentHeightWithoutButton = contentAreaScrollHeight - 64;
        }

        if (contentHeightWithoutButton < 0) {
          contentHeightWithoutButton = 0;
        }

        const totalHeight =
          backButtonHeight + contentHeightWithoutButton + buttonMarginTop + buttonHeight + 64;

        let shouldFix = false;
        if (this.viewportHeight > 0 && totalHeight > 0 && !isNaN(totalHeight)) {
          shouldFix = totalHeight < this.viewportHeight - 10;
        }
        this.shouldFixButton = shouldFix;
      });
    },
    handleResize() {
      const newViewportHeight = window.innerHeight;
      this.viewportHeight = newViewportHeight;
      this.checkIfShouldFixButton();
    },
  },
  watch: {
    linkedBankAccounts: {
      handler() {
        this.$nextTick(() => {
          this.checkIfShouldFixButton();
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Inter';
}
</style>
